import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Check, Clock, Coins, Globe2, Lock, Percent, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { SettingsCard, SettingsPage, SettingsRow, SettingsSection } from '@/components/settings/SettingsPage';
import { useAuth } from '@/hooks/useAuth';
import { settingsService, LocalizationSettings } from '@/services/settings.service';

/** "PK" → 🇵🇰 (regional-indicator pairs); anything else falls back to the globe icon. */
const flagOf = (code?: string | null) =>
  code && /^[A-Za-z]{2}$/.test(code) ? String.fromCodePoint(...code.toUpperCase().split('').map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : null;

/**
 * Country, currency and timezone are set by the platform for the company
 * (super-admin → Company); the company admin keeps control of VAT, which is
 * what actually differs between businesses.
 */
export default function LocalizationSettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const [data, setData] = useState<LocalizationSettings | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vat, setVat] = useState('');
  const [inclusive, setInclusive] = useState(false);

  const load = () => {
    setFailed(false);
    settingsService.getLocalization()
      .then((r) => { setData(r); setVat(r.defaultVatRatePct != null ? String(r.defaultVatRatePct) : ''); setInclusive(r.defaultVatInclusive); })
      .catch(() => setFailed(true));
  };
  useEffect(load, []);

  const savedVat = data?.defaultVatRatePct != null ? String(data.defaultVatRatePct) : '';
  const dirty = !!data && (vat.trim() !== savedVat || inclusive !== data.defaultVatInclusive);
  const vatNum = vat.trim() === '' ? null : Number(vat);
  const vatInvalid = vatNum !== null && (!Number.isFinite(vatNum) || vatNum < 0 || vatNum > 100);

  const save = async () => {
    if (vatInvalid) return;
    setSaving(true);
    try {
      const r = await settingsService.setLocalization({ defaultVatRatePct: vatNum, defaultVatInclusive: inclusive });
      setData(r);
      toast.success('VAT defaults saved');
    } catch { /* interceptor toasts */ } finally { setSaving(false); }
  };

  const flag = flagOf(data?.country);
  const effectiveVat = vatNum ?? data?.countryVatRatePct ?? null;

  return (
    <SettingsPage eyebrow="Workspace" title="Localisation" description="Where your business runs, the currency new prices start in, and the VAT that goes on new invoices and quotes.">
      {failed ? (
        <div className="py-7">
          <SettingsCard><p className="text-sm text-text-secondary">Localisation couldn't be loaded. <button type="button" onClick={load} className="font-medium text-primary hover:underline">Try again</button></p></SettingsCard>
        </div>
      ) : !data ? (
        <div className="space-y-4 py-7"><div className="skeleton h-44 rounded-2xl" /><div className="skeleton h-64 rounded-2xl" /></div>
      ) : (
        <>
          <SettingsSection
            title="Region"
            description="Set by Slightor for your account. Dates, times and new prices follow these."
            aside={<span className="inline-flex items-center gap-1.5 rounded-full bg-surface-alt px-2.5 py-1 text-2xs font-medium text-text-secondary"><Lock size={11} /> Contact support to change</span>}
          >
            <SettingsCard padded={false}>
              <div className="divide-y divide-border">
                <SettingsRow
                  icon={flag ? <span className="text-lg leading-none" aria-hidden>{flag}</span> : <Globe2 size={16} />}
                  label="Country"
                  value={data.countryLabel ?? data.country ?? 'Not set'}
                  locked
                />
                <SettingsRow icon={<Coins size={16} />} label="Currency" hint="New deals, documents and prices start in this currency." value={data.currency ?? '—'} locked />
                <SettingsRow icon={<Clock size={16} />} label="Timezone" hint="Reminders, reports and booking slots use it." value={(data.timezone ?? 'UTC').replace(/_/g, ' ')} locked />
              </div>
            </SettingsCard>
          </SettingsSection>

          <SettingsSection
            title="VAT on new documents"
            description={isAdmin ? 'Applied to every new invoice and quote. Existing documents keep the VAT they were made with.' : 'Only a company admin can change VAT.'}
            aside={effectiveVat !== null && (
              <div className="rounded-xl border border-border bg-surface px-3 py-2 text-xs text-text-secondary">
                Right now: <span className="font-semibold text-text-primary">{effectiveVat}%</span> {inclusive ? 'included in prices' : 'added on top'}
                {vatNum === null && data.countryVatRatePct != null && <span className="text-text-muted"> (country standard)</span>}
              </div>
            )}
          >
            <SettingsCard
              footer={isAdmin && dirty ? (
                <>
                  <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} onClick={() => { setVat(savedVat); setInclusive(data.defaultVatInclusive); }}>Discard</Button>
                  <Button size="sm" iconLeft={<Save size={14} />} loading={saving} disabled={vatInvalid} onClick={save}>Save VAT</Button>
                </>
              ) : undefined}
            >
              <div className="grid gap-6 @2xl:grid-cols-[180px_1fr]">
                <label className="block">
                  <span className="mb-1.5 block text-[13px] font-medium text-text-secondary">VAT rate</span>
                  <div className="relative">
                    <Input
                      type="number" min={0} max={100} step="0.1" value={vat} disabled={!isAdmin}
                      onChange={(e) => setVat(e.target.value)} invalid={vatInvalid}
                      placeholder={data.countryVatRatePct != null ? `${data.countryVatRatePct}` : '0'}
                      className="pr-9"
                    />
                    <Percent size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  </div>
                  <span className={clsx('mt-1.5 block text-2xs', vatInvalid ? 'text-danger' : 'text-text-muted')}>
                    {vatInvalid ? 'Enter a rate between 0 and 100.' : data.countryVatRatePct != null ? `Empty = country standard (${data.countryVatRatePct}%).` : 'Empty = no VAT.'}
                  </span>
                </label>

                <div>
                  <span className="mb-1.5 block text-[13px] font-medium text-text-secondary">How prices are shown</span>
                  <div className="grid gap-2">
                    {[
                      { v: false, title: 'Added on top', text: 'Prices exclude VAT; it\'s a separate line — usual in the Gulf.' },
                      { v: true, title: 'Included in prices', text: 'Prices already contain VAT — usual in Europe.' },
                    ].map((o) => {
                      const on = inclusive === o.v;
                      return (
                        <button key={String(o.v)} type="button" disabled={!isAdmin} onClick={() => setInclusive(o.v)} aria-pressed={on}
                          className={clsx('relative rounded-xl border p-3.5 text-left transition-colors disabled:cursor-default',
                            on ? 'border-primary bg-primary-soft/60' : 'border-border hover:border-border-strong')}>
                          {on && <span className="absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white"><Check size={12} /></span>}
                          <span className="block pr-6 text-sm font-semibold text-text-primary">{o.title}</span>
                          <span className="mt-0.5 block text-xs text-text-muted">{o.text}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </SettingsCard>
          </SettingsSection>
        </>
      )}
    </SettingsPage>
  );
}
